/**
 * Delivery timeline: replaces the business-day ranges rendered by Liquid with dates
 * counted from today (Saturday and Sunday skipped), e.g. "Oct 15th - Oct 17th".
 */
class DeliveryTimeline extends HTMLElement {
  connectedCallback() {
    const { shipMin, shipMax, deliverMin, deliverMax } = this.dataset;
    const today = new Date();

    this.#setText('order', DeliveryTimeline.format(today));
    this.#setText('ship', DeliveryTimeline.range(today, Number(shipMin), Number(shipMax)));
    this.#setText('deliver', DeliveryTimeline.range(today, Number(deliverMin), Number(deliverMax)));
  }

  /**
   * @param {string} step
   * @param {string} text
   */
  #setText(step, text) {
    const element = this.querySelector(`[data-step="${step}"]`);
    if (element && text) element.textContent = text;
  }

  /**
   * @param {Date} start
   * @param {number} min
   * @param {number} max
   * @returns {string}
   */
  static range(start, min, max) {
    if (!Number.isFinite(min) || !Number.isFinite(max)) return '';
    const from = DeliveryTimeline.format(DeliveryTimeline.addBusinessDays(start, min));
    const to = DeliveryTimeline.format(DeliveryTimeline.addBusinessDays(start, max));
    return from === to ? from : `${from} - ${to}`;
  }

  /**
   * @param {Date} start
   * @param {number} days
   * @returns {Date}
   */
  static addBusinessDays(start, days) {
    const date = new Date(start);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6) added += 1;
    }
    return date;
  }

  /**
   * @param {Date} date
   * @returns {string}
   */
  static format(date) {
    // Other storefront languages get their own date format ("28. Okt.", "10月28日"); English keeps "Oct 28th"
    const lang = document.documentElement.lang || 'en';
    if (!lang.toLowerCase().startsWith('en')) {
      return date.toLocaleDateString(lang, { month: 'short', day: 'numeric' });
    }
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const tens = day % 100;
    const suffix = tens >= 11 && tens <= 13 ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] ?? 'th';
    return `${month} ${day}${suffix}`;
  }
}

if (!customElements.get('delivery-timeline')) {
  customElements.define('delivery-timeline', DeliveryTimeline);
}
